/**
 * Central SEO metadata configuration for all routes.
 * Used as the single source of truth for page titles and descriptions.
 * 
 * The SEO/TechnicalSEO components on each page can override these values,
 * but this map serves as the canonical reference and fallback.
 */

export interface PageSEOData {
  title: string;
  description: string;
  keywords?: string;
  pageType?: 'homepage' | 'blog' | 'service' | 'location' | 'product' | 'contact' | 'about' | 'default';
}

export const seoData: Record<string, PageSEOData> = {
  // ─── Core Pages ───────────────────────────────────────────
  "/": {
    title: "Secondary Glazing London | Acoustic & Heritage Experts",
    description: "Bespoke secondary glazing in London for quieter, warmer period homes. Acoustic and heritage specialists offering surveys, installation and a 12-year warranty.",
    keywords: "secondary glazing london, noise reduction windows, thermal insulation, heritage window solutions, soundproof windows",
    pageType: "homepage",
  },
  "/secondary-glazing": {
    title: "Secondary Glazing Services London | Professional Window Solutions",
    description: "Expert secondary glazing services in London. Reduce noise by 80%, improve thermal insulation, preserve heritage windows. Free consultation & quote.",
    keywords: "secondary glazing services, window glazing london, noise reduction, thermal insulation",
    pageType: "service",
  },
  "/get-quote": {
    title: "Get a Free Secondary Glazing Quote",
    description: "Request a free, no-obligation quote for secondary glazing installation in London. Competitive pricing for noise reduction and thermal insulation solutions.",
    keywords: "secondary glazing quote, free quote, glazing prices london",
    pageType: "contact",
  },
  "/contact": {
    title: "Contact Us | Secondary Glazing London",
    description: "Get in touch with London's leading secondary glazing specialists. Call, email, or visit us for expert advice on noise reduction and thermal insulation.",
    keywords: "contact secondary glazing, london glazing company, window specialists",
    pageType: "contact",
  },

  // ─── Feature / Service Pages ──────────────────────────────
  "/secondary-glazing-benefits": {
    title: "Benefits of Secondary Glazing | Noise, Heat & Security",
    description: "Discover the key benefits of secondary glazing: up to 80% noise reduction, improved thermal insulation, enhanced security, and draft elimination.",
    pageType: "service",
  },
  "/secondary-glazing-cost": {
    title: "Secondary Glazing Cost Calculator | Instant London Estimates",
    description: "Estimate your secondary glazing project in seconds. Transparent m² pricing for acoustic, thermal, and heritage glass options. Get an instant London installation quote.",
    pageType: "service",
  },
  "/noise-reduction-secondary-glazing": {
    title: "Soundproof Secondary Glazing | 80% Noise Reduction | 10.8mm Glass",
    description: "Achieve up to 45dB (80%) noise reduction with 10.8mm acoustic laminate glass. The ultimate soundproofing solution for London homes near busy roads and rail.",
    pageType: "service",
  },
  "/thermal-insulation-secondary-glazing": {
    title: "Energy Saving Secondary Glazing | Reduce Heat Loss by 65%",
    description: "Slash heating bills and stop cold drafts. Our thermal secondary glazing creates a high-performance barrier that retains 65% more heat than single glazing alone.",
    pageType: "service",
  },
  "/heritage-preservation-secondary-glazing": {
    title: "Listed Building Secondary Glazing | Grade I & II Specialists",
    description: "Approved secondary glazing for Grade I & II listed buildings. Preserve original sash windows and architectural character while meeting modern efficiency standards.",
    pageType: "service",
  },
  "/double-glazed-secondary-glazing-units": {
    title: "Double Glazed Secondary Glazing Units London",
    description: "Premium double glazed secondary glazing units for maximum noise and thermal performance. Professional installation across London.",
    pageType: "product",
  },
  "/preventing-cold-air-infiltration": {
    title: "Preventing Cold Air Infiltration | Window Solutions",
    description: "Stop cold draughts and air infiltration through windows with professional secondary glazing. Improve comfort and cut energy bills.",
    pageType: "service",
  },
  "/secondary-glazing-alternative-to-double-glazing": {
    title: "Secondary Glazing vs Double Glazing | Which Is Better?",
    description: "Compare secondary glazing and double glazing: cost, performance, and suitability for listed buildings. Expert advice from London specialists.",
    pageType: "service",
  },
  "/secondary-glazing-drafty-windows": {
    title: "Fix Drafty Windows with Secondary Glazing",
    description: "Eliminate window draughts with professionally fitted secondary glazing. Fast installation, immediate comfort improvement in London homes.",
    pageType: "service",
  },
  "/secondary-glazing-enhancing-window-insulation": {
    title: "Enhance Window Insulation | Secondary Glazing",
    description: "Boost your window insulation with secondary glazing. Reduce heat loss by up to 65% without replacing original windows.",
    pageType: "service",
  },
  "/heritage-window-solutions": {
    title: "Heritage Window Solutions | Listed Building Glazing",
    description: "Specialist heritage window solutions for listed buildings and conservation areas in London. Preserve character while improving performance.",
    pageType: "service",
  },
  "/soundproof-windows": {
    title: "Soundproof Windows London | Noise Reduction Experts",
    description: "Professional soundproof windows in London. Achieve up to 45dB noise reduction with specialist secondary glazing systems.",
    pageType: "service",
  },
  "/acoustic-glazing": {
    title: "Acoustic Glazing London | Sound Insulation Windows",
    description: "High-performance acoustic glazing for London properties. Specialist sound insulation solutions for homes, offices, and studios.",
    pageType: "service",
  },
  "/window-energy-efficiency": {
    title: "Window Energy Efficiency | Reduce Heating Bills",
    description: "Improve window energy efficiency with secondary glazing. Cut heating costs by up to 30% and reduce your carbon footprint.",
    pageType: "service",
  },
  "/draught-proofing-windows": {
    title: "Draught Proofing Windows London | Stop Cold Air",
    description: "Professional window draught proofing in London. Stop cold drafts, reduce energy bills by 30%, and improve home comfort.",
    pageType: "service",
  },
  "/period-property-windows": {
    title: "Period Property Windows | Glazing for Older Homes",
    description: "Specialist secondary glazing for period property windows. Preserve original features while adding modern thermal and acoustic performance.",
    pageType: "service",
  },
  "/listed-building-secondary-glazing-guide": {
    title: "Listed Building Secondary Glazing Guide",
    description: "Comprehensive guide to secondary glazing for listed buildings. Planning permission, conservation area rules, and approved installation methods.",
    pageType: "service",
  },

  // ─── Product Pages ────────────────────────────────────────
  "/fixed-units-glazing": {
    title: "Fixed Secondary Glazing Units London",
    description: "Fixed secondary glazing panels for maximum noise reduction and thermal performance. Ideal for windows that don't need to open.",
    pageType: "product",
  },
  "/sliding-units-glazing": {
    title: "Sliding Secondary Glazing Units London",
    description: "Horizontal sliding secondary glazing for easy access and ventilation. Perfect for sash and casement windows in London homes.",
    pageType: "product",
  },
  "/hinged-units-glazing": {
    title: "Hinged Secondary Glazing Units London",
    description: "Hinged secondary glazing panels for full window access. Ideal for cleaning and fire escape requirements in London properties.",
    pageType: "product",
  },
  "/lift-out-units-glazing": {
    title: "Lift-Out Secondary Glazing Units London",
    description: "Removable lift-out secondary glazing panels. Seasonal flexibility with excellent noise reduction and thermal insulation.",
    pageType: "product",
  },
  "/vertical-sliding-units-glazing": {
    title: "Secondary Glazing for Sash Windows | Slim-Line Sliders",
    description: "Slim-line vertical sliding secondary glazing designed specifically for London sash windows. Smooth operation, easy cleaning access, and invisible from the street.",
    pageType: "product",
  },
  "/horizontal-sliding-units-glazing": {
    title: "Horizontal Sliding Secondary Glazing London",
    description: "Horizontal sliding secondary glazing for wide window openings. Smooth operation with excellent acoustic and thermal performance.",
    pageType: "product",
  },
  "/secondary-glazing-images": {
    title: "Secondary Glazing Gallery | Installation Photos",
    description: "Browse our gallery of secondary glazing installations across London. See real examples of heritage, residential, and commercial projects.",
    pageType: "default",
  },
  "/case-studies": {
    title: "Secondary Glazing Case Studies | Real Projects",
    description: "Explore our secondary glazing case studies from across London. Real results showing noise reduction, thermal improvement, and heritage preservation.",
    pageType: "default",
  },
  "/faq": {
    title: "Secondary Glazing FAQ | Common Questions",
    description: "Answers to common questions about secondary glazing: costs, installation, noise reduction, thermal performance, and planning permission.",
    pageType: "default",
  },

  // ─── Location Pages ───────────────────────────────────────
  "/chelsea-secondary-glazing": {
    title: "Secondary Glazing Chelsea (SW3) | Heritage Experts",
    description: "Bespoke secondary glazing for Chelsea (SW3) period homes. Heritage-approved solutions for Cadogan Estate and RBKC conservation areas.",
    keywords: "secondary glazing chelsea, SW3 glazing, cadogan estate windows",
    pageType: "location",
  },
  "/chelsea-secondary-glazing-guide": {
    title: "Secondary Glazing Chelsea | SW3 Soundproofing & Luxury",
    description: "Silent luxury for Chelsea SW3. Specialist secondary glazing for period mansion blocks and townhouses. Compliant with Cadogan Estate heritage requirements.",
    keywords: "secondary glazing chelsea, SW3 soundproofing, chelsea heritage windows, king's road noise reduction, cadogan estate glazing",
    pageType: "location",
  },
  "/belgravia-secondary-glazing": {
    title: "Secondary Glazing Belgravia | Grosvenor Estate Approved",
    description: "Bespoke secondary glazing for Belgravia's Grade II listed properties. Grosvenor Estate-compliant solutions for SW1X homes.",
    pageType: "location",
  },
  "/mayfair-secondary-glazing": {
    title: "Secondary Glazing Mayfair | W1 Heritage Specialists",
    description: "Premium secondary glazing for Mayfair's prestigious properties. Discreet installation preserving the elegance of W1 period homes.",
    pageType: "location",
  },
  "/knightsbridge-secondary-glazing": {
    title: "Secondary Glazing Knightsbridge | SW7 Solutions",
    description: "Expert secondary glazing for Knightsbridge apartments and townhouses. Noise reduction and thermal performance for SW7 properties.",
    pageType: "location",
  },
  "/kensington-secondary-glazing": {
    title: "Secondary Glazing Kensington | RBKC Heritage Specialists",
    description: "Bespoke secondary glazing for Grade II listed Victorian homes in Kensington. Heritage-approved solutions for RBKC conservation areas. Eliminate drafts and noise.",
    pageType: "location",
  },
  "/marylebone-secondary-glazing": {
    title: "Secondary Glazing Marylebone | W1 Noise Reduction",
    description: "Secondary glazing for Marylebone's period properties. Howard de Walden Estate-compliant solutions for Harley Street and surrounds.",
    pageType: "location",
  },
  "/marylebone-secondary-glazing-guide": {
    title: "Secondary Glazing Marylebone | W1 Heritage Glazing",
    description: "Discreet secondary glazing for Marylebone residences. Professional soundproofing for W1 properties near Harley St and Regent's Park. Howard de Walden compliant.",
    pageType: "location",
  },
  "/paddington-secondary-glazing": {
    title: "Secondary Glazing Paddington | W2 Specialists",
    description: "Professional secondary glazing for Paddington properties. Reduce railway and traffic noise with expert W2 installation services.",
    pageType: "location",
  },
  "/hampstead-secondary-glazing": {
    title: "Secondary Glazing Hampstead | NW3 Conservation Experts",
    description: "Specialist glazing for Hampstead's historic cottages and villas. Expert insulation for NW3 properties within the Heath's strict conservation boundaries.",
    pageType: "location",
  },
  "/highgate-secondary-glazing": {
    title: "Secondary Glazing Highgate | N6 Period Properties",
    description: "Specialist secondary glazing for Highgate's Victorian and Georgian homes. Conservation-sensitive solutions for N6 properties.",
    pageType: "location",
  },
  "/notting-hill-secondary-glazing": {
    title: "Secondary Glazing Notting Hill | W11 Specialists",
    description: "Expert secondary glazing for Notting Hill's iconic townhouses. Heritage-approved solutions for W11 conservation areas.",
    pageType: "location",
  },
  "/holland-park-secondary-glazing": {
    title: "Secondary Glazing Holland Park | W14 Solutions",
    description: "Premium secondary glazing for Holland Park's prestigious homes. Discreet noise reduction and thermal insulation for W14.",
    pageType: "location",
  },
  "/fulham-secondary-glazing": {
    title: "Secondary Glazing Fulham | SW6 Window Solutions",
    description: "Professional secondary glazing for Fulham properties. Reduce noise and improve thermal performance in SW6 Victorian terraces.",
    pageType: "location",
  },
  "/chiswick-secondary-glazing": {
    title: "Secondary Glazing Chiswick | W4 Heritage Experts",
    description: "Specialist secondary glazing for Chiswick's period homes. Expert solutions for W4 conservation areas and listed buildings.",
    pageType: "location",
  },
  "/st-johns-wood-secondary-glazing": {
    title: "Secondary Glazing St John's Wood | NW8 Specialists",
    description: "Premium secondary glazing for St John's Wood properties. Acoustic and thermal solutions for NW8's fine period residences.",
    pageType: "location",
  },
  "/soho-secondary-glazing": {
    title: "Secondary Glazing Soho | W1 Noise Reduction",
    description: "Expert secondary glazing for Soho properties. Critical noise reduction for W1's vibrant entertainment district.",
    pageType: "location",
  },
  "/covent-garden-secondary-glazing": {
    title: "Secondary Glazing Covent Garden | WC2 Solutions",
    description: "Professional secondary glazing for Covent Garden apartments. Noise reduction essential for WC2's busy central location.",
    pageType: "location",
  },
  "/bloomsbury-secondary-glazing": {
    title: "Secondary Glazing Bloomsbury | WC1 Heritage Homes",
    description: "Specialist secondary glazing for Bloomsbury's Georgian townhouses. Conservation-approved solutions for WC1 listed buildings.",
    pageType: "location",
  },
  "/ealing-secondary-glazing": {
    title: "Secondary Glazing Ealing | W5 Window Specialists",
    description: "Professional secondary glazing for Ealing properties. Reduce Heathrow flight noise and improve thermal performance in W5.",
    pageType: "location",
  },
  "/kew-secondary-glazing": {
    title: "Secondary Glazing Kew | TW9 Heritage Solutions",
    description: "Bespoke secondary glazing for Kew's conservation area properties. Sympathetic solutions near Kew Gardens in TW9.",
    pageType: "location",
  },
  "/kingston-secondary-glazing": {
    title: "Secondary Glazing Kingston | KT1 Window Solutions",
    description: "Expert secondary glazing for Kingston upon Thames properties. Noise reduction and thermal insulation for KT1 homes.",
    pageType: "location",
  },
  "/isleworth-secondary-glazing": {
    title: "Secondary Glazing Isleworth | TW7 Specialists",
    description: "Professional secondary glazing for Isleworth properties. Reduce flight path noise with expert installation in TW7.",
    pageType: "location",
  },
  "/richmond-secondary-glazing": {
    title: "Secondary Glazing Richmond | TW9 Heritage Homes",
    description: "Premium secondary glazing for Richmond's conservation area properties. Specialist solutions for TW9 and TW10 period homes.",
    pageType: "location",
  },

  // ─── Blog & Content Pages ────────────────────────────────
  "/blog-secondary-glazing": {
    title: "Secondary Glazing Blog | Expert Guides & Tips",
    description: "Expert articles on secondary glazing: installation guides, cost comparisons, noise reduction tips, and heritage window advice.",
    pageType: "blog",
  },
  "/blog/secondary-glazing-listed-buildings-guide": {
    title: "Listed Building Secondary Glazing Guide",
    description: "The definitive guide to secondary glazing for Grade I & II listed buildings in London. 70% noise reduction, fully reversible, conservation-approved.",
    keywords: "secondary glazing listed buildings, heritage window soundproofing, listed building consent glazing, conservation area windows",
    pageType: "blog",
  },
  "/blog-content-calendar": {
    title: "Content Calendar | Secondary Glazing Blog",
    description: "Upcoming articles and guides on secondary glazing topics. Stay informed about noise reduction, thermal insulation, and heritage solutions.",
    pageType: "blog",
  },
  "/free-resources": {
    title: "Free Secondary Glazing Resources & Guides",
    description: "Download free guides on secondary glazing: cost calculators, noise reduction guides, heritage compliance checklists, and more.",
    pageType: "default",
  },

  // ─── Legal Pages ──────────────────────────────────────────
  "/privacy-policy": {
    title: "Privacy Policy",
    description: "Our privacy policy explains how London Secondary Glazing collects, uses, and protects your personal data.",
    pageType: "default",
  },
  "/terms-conditions": {
    title: "Terms & Conditions",
    description: "Terms and conditions for London Secondary Glazing services, including installation guarantees and warranty information.",
    pageType: "default",
  },
  "/cookie-policy": {
    title: "Cookie Policy",
    description: "How London Secondary Glazing uses cookies to improve your browsing experience and website performance.",
    pageType: "default",
  },
  "/accessibility-statement": {
    title: "Accessibility Statement",
    description: "Our commitment to making London Secondary Glazing's website accessible to all users regardless of ability.",
    pageType: "default",
  },
  "/disclaimer": {
    title: "Disclaimer",
    description: "Legal disclaimer for London Secondary Glazing. Information about our services, pricing, and website content.",
    pageType: "default",
  },

  // ─── Locations / Service Areas ────────────────────────────
  "/locations": {
    title: "Service Areas | Secondary Glazing Across London",
    description: "We install secondary glazing across all London boroughs. Find your local area for specialist noise reduction and thermal insulation services.",
    pageType: "location",
  },
  "/islington-secondary-glazing": {
    title: "Secondary Glazing Islington N1",
    description: "Georgian & Victorian secondary glazing in Islington N1. 60% heat retention, 75% noise reduction. Conservation-approved for Canonbury & Upper Street.",
    keywords: "secondary glazing islington, N1 sash window soundproofing, islington georgian windows, canonbury secondary glazing",
    pageType: "location",
  },
  "/mayfair-st-james-secondary-glazing": {
    title: "Secondary Glazing Mayfair & St James's | Ultra-Prime",
    description: "Discreet secondary glazing for Mayfair & St James's ultra-prime properties. Grosvenor Estate compliant, 54dB noise reduction. W1 heritage specialists.",
    keywords: "secondary glazing mayfair, W1 heritage window solutions, grosvenor estate glazing, st james secondary glazing, park lane noise reduction",
    pageType: "location",
  },
  "/sitemap": {
    title: "Sitemap | All Pages",
    description: "Complete sitemap of London Secondary Glazing. Browse all our service pages, location guides, and resources.",
    pageType: "default",
  },
};

/**
 * Get SEO data for a given route path.
 * Returns undefined if no specific data is configured (page should set its own).
 */
export function getSEODataForRoute(path: string): PageSEOData | undefined {
  // Exact match first
  if (seoData[path]) return seoData[path];
  
  // Try without trailing slash
  const normalized = path.endsWith('/') && path !== '/' ? path.slice(0, -1) : path;
  return seoData[normalized];
}
